import SignType from "../../../../domain/signType";
import { usd } from "../../../../util/formatCurrency.js";

export const getProductId = (p) => p.product_id;

export const getSignedType = (p) => p.signed_type;

export const getFormattedPrice = (p) => usd(p._totalPrice);

export const isCustomImageUpload = (p) => p.product_type_id === 400;

export const isGiftPrint = (p) =>
  p.product_type_id === 323 ||
  p.product_type_id === 338 ||
  (typeof p.product_type === "string" &&
    p.product_type.toLowerCase().includes("gift print"));

export const isAnySendin = (p) =>
  Boolean(p.sendin || p.sendin_no_kit || p.sendin_international);

export const isSoldOut = (p) =>
  p.stock !== null && Number.isInteger(p.stock) && p.stock <= 0;

export const isPresigned = (p) =>
  Boolean(p.presigned) || p.signed_type === "Presigned";

export const isUnsigned = (p) => p.signed_type === "No Autograph";

export const isVaulted = (p) =>
  Boolean(p.vaulted_until) && new Date(p.vaulted_until) > new Date();

export const isDigitalSignature = (p) => p.signed_type === "Digital Signature";

export const allowPersonalization = (p) => Boolean(p.allow_personalization);

export const allowNotes = (p) => Boolean(p.allow_notes);

export const getAspectRatio = (p) => p.aspect_ratio ?? 1;

export const hasVariants = (p) =>
  Array.isArray(p._variants) && p._variants.length > 1;

export const cannotBePurchased = (p) => isSoldOut(p) || isVaulted(p);

/** Canonical list of fields a variant object carries. */
export const VARIANT_FIELDS = [
  "product_id",
  "name",
  "subtext",
  "image_url",
  "product_type",
  "signed_type",
  "_totalPrice",
  "stock",
  "vaulted_until",
  "allow_personalization",
  "allow_notes",
];

export const purchasableOrAll = (variants) => {
  const purchasable = variants.filter((v) => !cannotBePurchased(v));
  return purchasable.length > 0 ? purchasable : variants;
};

const mostExpensive = (variants) =>
  variants.reduce(
    (max, v) => ((v._totalPrice ?? 0) > (max._totalPrice ?? 0) ? v : max),
    variants[0],
  );

export const pickBestVariant = (variants) => {
  const pool = purchasableOrAll(variants);
  return (
    pool.find((v) => v.signed_type === SignType.livestream) ??
    mostExpensive(pool)
  );
};

export const getBestVariant = (p) =>
  Array.isArray(p._variants) && p._variants.length > 0
    ? pickBestVariant(p._variants)
    : null;

export function getSignedTypeDescription(p) {
  const signedType = p.signed_type;
  if (!signedType) return null;
  const isPersonalized = Boolean(p.allow_personalization || p.allow_notes);
  return SignType.getSignedTypeDescription({ signedType, isPersonalized });
}

export function resolveProduct(product, variant) {
  if (!variant) return product;
  const overrides = VARIANT_FIELDS.reduce((acc, key) => {
    acc[key] = variant[key];
    return acc;
  }, {});
  return { ...product, ...overrides, _variants: null };
}

export function getStockIndicator(stockValue) {
  if (!Number.isFinite(stockValue) || stockValue <= 0) return null;
  if (stockValue <= 5) {
    return { text: `Only ${stockValue} left`, tier: "critical" };
  }
  if (stockValue <= 30) {
    return { text: "Limited quantity", tier: "low" };
  }
  return null;
}

const SPEED_ORDER = { fast: 0, moderate: 1, slow: 2 };

const speedAttributes = {
  fast: (isMultisign) => ({
    speed: "fast",
    text: "Fast",
    tooltip: isMultisign
      ? "Ships fast! These talent are quick to fulfill their orders."
      : "Ships fast! This talent is quick to fulfill orders.",
  }),
  moderate: (isMultisign) => ({
    speed: "moderate",
    text: "Moderate",
    tooltip: isMultisign
      ? "Ships on time! These talent typically meet deadlines, but may require more time due to their schedules."
      : "Ships on time! This talent typically meets deadlines, but may require more time due to their schedule.",
  }),
  slow: (isMultisign) => ({
    speed: "slow",
    text: "Slow",
    tooltip: isMultisign
      ? "Ships slowly! These talent may take longer to fulfill their orders due to their schedules."
      : "Ships slowly! This talent may take longer to fulfill orders due to their schedule.",
  }),
};

export function getFulfillmentSpeed(product) {
  const fulfillers = product?.fulfillers;
  if (!Array.isArray(fulfillers) || fulfillers.length === 0) return null;

  const speeds = fulfillers
    .map((f) => f.fulfillment_speed)
    .filter((s) => s && s !== "hidden" && s in SPEED_ORDER);

  if (speeds.length === 0) return null;

  const isMultisign = fulfillers.length > 1;
  const slowest = speeds.reduce((worst, s) =>
    SPEED_ORDER[s] > SPEED_ORDER[worst] ? s : worst,
  );

  const attrs = speedAttributes[slowest];
  return attrs ? { ...attrs(isMultisign), isMultisign } : null;
}

export const shippingTextFor = (resolved) =>
  resolved?.no_shipping
    ? "Digital delivery"
    : resolved?.free_shipping
    ? "Free shipping"
    : resolved?.domestic_only
    ? "US shipping only"
    : "Shipping calculated at checkout";

export const cartLabelFor = ({ isAddingToCart, soldOut, cartTotal }) =>
  isAddingToCart
    ? null
    : soldOut
    ? "Sold Out"
    : `Add to Cart \u00B7 ${usd(cartTotal)}`;

export const calculateState = ({
  product,
  state,
  isAddingToCart,
  errorMessage,
}) => {
  const resolved = product
    ? resolveProduct(product, state.selectedVariant)
    : null;
  const resolvedProductId = resolved?.product_id;

  const needsVariant = product
    ? hasVariants(product) && !state.selectedVariant
    : false;
  const needsImage = resolved
    ? isCustomImageUpload(resolved) && !state.customerImage
    : false;
  const soldOut = resolved ? cannotBePurchased(resolved) : false;
  const cartDisabled =
    isAddingToCart ||
    Boolean(errorMessage) ||
    needsVariant ||
    needsImage ||
    soldOut;

  return { resolved, resolvedProductId, soldOut, cartDisabled };
};

const signersOf = ({ fulfillers, influencer_name, route }) =>
  (fulfillers ?? []).length === 0
    ? [{ name: influencer_name, route }]
    : fulfillers.filter((f) => !f.hasNoFulfillment && !f.no_fulfillment);

export const getSignerLinks = (p) =>
  signersOf(p)
    .filter(({ name }) => name)
    .map(({ name, route }) => ({ name, to: `/${route}` }));
